/* <localization-picker>: a button that opens a list of countries or languages.
   Choosing an option is handled by <localization-form> in theme.js, which submits the localization form.
   Phones (live): the list opens as a sheet at the bottom of the screen over a 40% veil, above any open drawer
   (it goes to the top layer with the Popover API), with a round close button above it. */
const phoneQuery = window.matchMedia('(max-width: 699px)');
const canTopLayer = typeof HTMLElement.prototype.showPopover === 'function';
class LocalizationPicker extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-toggle]');
    this.panel = this.querySelector('.localization__popover');
    if (!this.button || !this.panel) return;
    this.onButtonClick = () => (this.panel.hidden ? this.open() : this.close());
    // Phone sheet: the close button and a tap on the veil (the panel itself, outside the sheet) close it.
    this.onPanelClick = (event) => {
      if (event.target === this.panel || event.target.closest('[data-picker-close]')) { event.stopPropagation(); this.close(); this.button.focus({ preventScroll: true }); }
    };
    this.panel.addEventListener('click', this.onPanelClick);
    this.onDocumentClick = (event) => { if (!this.panel.hidden && !this.contains(event.target)) this.close(); };
    this.onKeydown = (event) => {
      if (event.key === 'Escape' && !this.panel.hidden) {
        event.stopPropagation();
        this.close();
        this.button.focus();
      }
    };
    this.onFocusOut = (event) => { if (!this.panel.hidden && event.relatedTarget && !this.contains(event.relatedTarget)) this.close(); };
    // Another picker opened, a dialog opened or the page was swapped: close this list.
    this.onOtherOpen = (event) => { if (!event.detail || event.detail !== this) this.close(); };
    this.button.addEventListener('click', this.onButtonClick);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('focusout', this.onFocusOut);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('localization:open', this.onOtherOpen);
    document.addEventListener('dialog:open', this.onOtherOpen);
    document.addEventListener('tt:navigated', this.onOtherOpen);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onButtonClick);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('localization:open', this.onOtherOpen);
    document.removeEventListener('dialog:open', this.onOtherOpen);
    document.removeEventListener('tt:navigated', this.onOtherOpen);
  }

  open() {
    document.dispatchEvent(new CustomEvent('localization:open', { detail: this }));
    this.panel.hidden = false;
    if (phoneQuery.matches && canTopLayer) {
      this.panel.setAttribute('popover', 'manual');
      this.panel.classList.add('is-sheet');
      try { this.panel.showPopover(); } catch (e) { /* already open */ }
    }
    this.button.setAttribute('aria-expanded', 'true');
    const current = this.panel.querySelector('[aria-current="true"]') || this.panel.querySelector('.localization__option');
    // Live opens the list at its top; focus goes to the current option without scrolling to it.
    if (current) current.focus({ preventScroll: true });
  }

  close() {
    if (!this.panel || this.panel.hidden) return;
    if (this.panel.hasAttribute('popover')) {
      try { this.panel.hidePopover(); } catch (e) { /* not open */ }
      this.panel.removeAttribute('popover');
      this.panel.classList.remove('is-sheet');
    }
    this.panel.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
  }
}
if (!customElements.get('localization-picker')) customElements.define('localization-picker', LocalizationPicker);
